import { useEffect, useRef, useState } from 'react'
import { DateRangeButton, DEFAULT_PRESETS, presetSpan } from '../console/DateRangeButton'
import { Button } from '../console/ui'
import { IconChevronDown, IconDownload } from '../console/icons'

/**
 * The page-level period filter, drawn as the console's date-range button plus
 * an Export menu (Figma: Overview page header).
 *
 * ⚠️ SAME PROPS AS BEFORE, SO EVERY CALLER KEEPS WORKING. It used to be two
 * native date inputs, an "Apply Filter" and a "Clear" button; the redesign
 * applies a period the moment it is chosen (a preset, or the second day picked
 * on the calendar), so `onApply` now fires straight from the picker and
 * `onClear` maps to "back to the last 30 days".
 */
export interface PageDateFilterBarProps {
  fromDate: string
  toDate: string
  onApply: (from: string, to: string) => void
  onClear?: () => void
  onExportExcel?: () => void
  onExportCsv?: () => void
  /** Kept for callers; the console header carries its own page title. */
  title?: string
  /** Kept for callers; presets now live inside the picker. */
  presetRanges?: boolean
  variant?: 'card' | 'inline'
}

export function PageDateFilterBar({
  fromDate,
  toDate,
  onApply,
  onClear,
  onExportExcel,
  onExportCsv,
  variant = 'card',
}: PageDateFilterBarProps) {
  const [from, setFrom] = useState(fromDate)
  const [to, setTo] = useState(toDate)
  useEffect(() => { setFrom(fromDate) }, [fromDate])
  useEffect(() => { setTo(toDate) }, [toDate])

  const apply = (f: string, t: string) => {
    setFrom(f)
    setTo(t)
    onApply(f, t)
  }

  const reset = () => {
    const s = presetSpan(30)
    setFrom(s.from)
    setTo(s.to)
    if (onClear) onClear()
    else onApply(s.from, s.to)
  }

  const isDefault = (() => {
    const s = presetSpan(30)
    return s.from === from && s.to === to
  })()

  const canExport = Boolean(onExportExcel ?? onExportCsv)

  return (
    <div
      className={
        variant === 'inline'
          ? 'ml-auto flex flex-wrap items-center justify-end gap-2 font-dash'
          : 'flex flex-wrap items-center justify-end gap-2 font-dash'
      }
    >
      <DateRangeButton from={from} to={to} presets={DEFAULT_PRESETS} onChange={apply} />
      {!isDefault ? (
        <Button variant="ghost" onClick={reset}>
          Reset
        </Button>
      ) : null}
      {canExport ? <ExportMenu onExcel={onExportExcel} onCsv={onExportCsv} /> : null}
    </div>
  )
}

function ExportMenu({ onExcel, onCsv }: { onExcel?: () => void; onCsv?: () => void }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={root} className="relative">
      <Button icon={IconDownload} trailingIcon={IconChevronDown} aria-haspopup="menu" aria-expanded={open} onClick={() => { setOpen((o) => !o) }}>
        Export
      </Button>
      {open ? (
        <div role="menu" className="absolute top-full right-0 z-30 mt-1.5 w-52 rounded-xl border border-line bg-surface p-1.5 shadow-raised">
          {onExcel ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onExcel()
                setOpen(false)
              }}
              className="flex h-8 w-full items-center rounded-md px-2.5 text-left text-[13px] text-ink hover:bg-surface-muted"
            >
              Excel (.xlsx)
            </button>
          ) : null}
          {onCsv ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                onCsv()
                setOpen(false)
              }}
              className="flex h-8 w-full items-center rounded-md px-2.5 text-left text-[13px] text-ink hover:bg-surface-muted"
            >
              CSV (.csv)
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
